<script setup lang="ts">
import type { ConcreteComponent } from 'vue'
import type { MenuItem } from '~/types'

interface Props {
  menuItem: MenuItem.Model
  isTopLevel?: boolean
}

const props = defineProps<Props>()
const { menuItem } = toRefs(props)
const {
  componentForMenuItem,
  name,
  children,
  hasSubMenu,
  isColumnMenuItem,
  isOpen,
  isTopMenuOpened,
  isStaticUrlMenuItem,
  isTextMenuItem,
  handleClickOnTopMenu,
  handleMouseOver,
  handleMouseLeave,
  setTopMenuId,
} = useMenuItem(menuItem, props.isTopLevel)

const component = computed<ConcreteComponent | string>(() => resolveComponent(componentForMenuItem.value))
const isExist = computed<boolean>(() => typeof component.value !== 'string')
</script>

<template>
  <li
    class="menu-item"
    :class="{
      'menu-item--has-submenu': hasSubMenu,
      'menu-item--static-url': isStaticUrlMenuItem,
      'menu-item--text': isTextMenuItem,
      'menu-item--open': isOpen || isTopMenuOpened,
    }"
    :aria-haspopup="hasSubMenu"
    :aria-expanded="isOpen || isTopMenuOpened"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
  >
    <Component
      :is="component"
      v-if="isExist"
      :menu-item="menuItem"
      @click="handleClickOnTopMenu"
    />
    <ul
      v-if="!isColumnMenuItem && hasSubMenu"
      class="menu-item__submenu"
    >
      <li class="menu-item__submenu-wrapper">
        <ul class="menu-item__submenu-inner">
          <li class="menu-item__submenu-item">
            <UIButton
              v-if="hasSubMenu && isTopMenuOpened"
              prepended-icon="raphael:arrowleft"
              variant="clear"
              class="menu-item__submenu-title"
              @click.prevent="setTopMenuId(null)"
            >
              {{ name }}
            </UIButton>
            <UIButton
              v-if="hasSubMenu && isTopMenuOpened"
              is-label-hidden
              prepended-icon="ant-design:close-outlined"
              variant="clear"
              class="menu-item__submenu-close"
              @click.prevent="setTopMenuId(null)"
            >
              Close Dropdown
            </UIButton>
            <MenuList
              :menu-items="children"
            />
          </li>
        </ul>
      </li>
    </ul>
  </li>
</template>

<style lang="scss">
  .menu-item {
    @apply flex flex-col items-center;
    @apply xl:h-full xl:flex-row;

    &--static-url,
    &--text {
      @apply mb-[15px];
    }

    &--static-url > a,
    &--text > span,
    &--text > button {
      @apply text-[15px] text-white/80 leading-[1] decoration-transparent underline underline-offset-4 transition-colors duration-300;
      @apply hover:text-white hover:decoration-white;
    }

    &__top-level {
      @apply mb-0 w-full;
      @apply xl:w-auto;
    }

    &__top-level > &__submenu {
      @apply absolute flex left-full top-0 w-full bg-primary py-[40px] mt-[20px] px-[20px];
      @apply xl:left-0 xl:top-full xl:py-[60px] xl:mt-0;
      @apply mx-auto max-w-full w-full px-[20px];
    }

    &__top-level > &__submenu > &__submenu-wrapper {
      @apply mx-auto max-w-full w-full;
      @apply lg:px-[20px];
      @apply xl:max-w-[1380px];
      @apply 2xl:max-w-[1480px];
    }

    &__top-level > &__submenu > &__submenu-wrapper > &__submenu-inner {
      @apply flex w-full relative;
    }

    &__top-level > a,
    &__top-level > span,
    &__top-level > button {
      @apply text-[28px] text-white font-semibold relative uppercase transition-colors duration-300 w-full flex items-center no-underline cursor-pointer py-[5px];
      @apply xl:text-[14px] xl:font-medium xl:w-auto xl:inline-block;
      @apply before:content-[''] before:w-0 before:h-[2px] before:bg-white before:block before:transition-[width] before:duration-300 before:absolute before:-bottom-[2px] before:left-0;
      @apply hover:before:w-full;
    }

    &__top-level:is(&--has-submenu) > a,
    &__top-level:is(&--has-submenu) > span,
    &__top-level:is(&--has-submenu) > button {
      @apply after:content-[''] after:w-[8px] after:h-[8px] after:ml-auto after:inline-block after:-rotate-90 after:border-t-[8px] after:border-x-[5px] after:border-t-white/60 after:border-x-transparent after:transition-transform after:duration-300;
      @apply xl:after:rotate-0 xl:after:ml-[10px];
      @apply hover:before:w-[calc(100%-20px)];
    }

    &__top-level:not(&--open) > &__submenu {
      @apply hidden;
    }

    &--has-submenu:is(&--open) > a,
    &--has-submenu:is(&--open) > span,
    &--has-submenu:is(&--open) > button {
      @apply after:rotate-180;
    }

    &__submenu-item {
      @apply flex flex-wrap text-center w-full;
      @apply xl:text-left;
    }

    &__submenu-item .menu-list > .menu-item {
      @apply items-start h-auto;
      @apply xl:flex-col;
    }

    &__submenu-item > .menu-list > .menu-item {
      @apply w-full;
      @apply xl:w-1/4;
    }

    &__submenu-item > .menu-list > .menu-item:not(.menu-item--static-url) {
      @apply mb-[20px];
      @apply xl:mb-[30px];
    }

    &__submenu-item .menu-list .menu-item &__submenu-item .menu-item {
      @apply w-full;
    }

    &__submenu-item .menu-list .menu-list {
      @apply flex-col;
    }

    &__submenu .menu-list {
      @apply flex flex-wrap w-full items-start gap-2;
    }

    &__submenu .menu-list &--text > span {
      @apply text-[15px] text-white font-semibold mb-[20px] leading-[1];
    }

    &__submenu-title,
    &__submenu-close {
      @apply absolute right-0 -top-[50px] text-white bg-transparent h-auto p-0;
      @apply lg:-right-[20px];
      @apply xl:right-0;

      .button__icon {
        @apply text-white;
      }
    }

    &__submenu-title {
      @apply right-auto -left-[5px] align-baseline mb-[40px] text-white text-[18px] font-medium uppercase;
      @apply xl:hidden;

      .button__icon {
        @apply w-[20px] h-[20px];
      }
    }

    &__submenu-close {
      @apply -top-[60px];

      .button__icon {
        @apply w-[30px] h-[30px] transition-transform duration-300;
      }

      &:hover .button__icon {
        @apply text-white scale-110;
      }
    }
  }
</style>
